import React, {useMemo} from "react";

import cx from "classnames";
import {difference} from "lodash";

import {useUser} from "@globals/user";
import {
  getAllDayPartsForUser,
  getDayPartLocationsForLocation,
} from "@globals/user/dayPartUtils";
import {DayPart, LocationType} from "@globals/user/interfaces";

import CustomSelect from "@components/forms/inputs/CustomSelect";

import {CustomSelectProps} from "../CustomSelect/CustomSelect";
import styles from "./DayPartSelect.scss";

type DayPartSelectProps = Omit<
  CustomSelectProps,
  "options" | "label" | "value" | "onChange"
> & {
  label?: string;
  value?: number;
  onChange?: (value: number) => void;
  filter?: (location: LocationType) => boolean;
  shouldChangeUserLocation?: boolean;
};

type SelectOption = {
  label: DayPart;
  value: number;
  isDisabled?: boolean;
};

export default function DayPartSelect({
  label = "Meal",
  placeholder = "Select your meal",
  filter = () => true,
  onChange,
  shouldChangeUserLocation,
  className,
  value,
  ...otherProps
}: DayPartSelectProps): JSX.Element | null {
  const {user, setLocation, isLocationLoading, hasAccessToMultipleDayParts} =
    useUser();

  const handleLocationChange = (newValue: number) => {
    // prevent unnecessary updates for the same option;
    if (value === newValue) {
      return;
    }
    onChange?.(newValue);
    if (shouldChangeUserLocation) {
      setLocation?.({id: newValue});
    }
  };

  const locations = useMemo(() => {
    if (!user) {
      return [];
    }

    const allUserDayParts = getAllDayPartsForUser(user);
    const activeDayParts = new Set();
    const options: SelectOption[] = getDayPartLocationsForLocation(user, value)
      .filter(filter)
      .map((location) => {
        activeDayParts.add(location.day_part);
        return {
          label: location.day_part,
          value: location.id,
        } as SelectOption;
      });

    // Add disabled meal periods
    if (allUserDayParts.size > activeDayParts.size) {
      const disabledDayParts = difference(
        Array.from(allUserDayParts),
        Array.from(activeDayParts)
      ) as DayPart[];
      for (const dayPart of disabledDayParts) {
        options.push({
          label: dayPart,
          value: -1,
          isDisabled: true,
        });
      }
    }
    return options;
  }, [user?.locations_list, user?.selected_location, filter]);

  if (!hasAccessToMultipleDayParts || locations.length <= 0) {
    return null;
  }

  return (
    <CustomSelect
      {...otherProps}
      isLoading={shouldChangeUserLocation && isLocationLoading}
      className={cx(styles.locationSelect, className)}
      label={label}
      placeholder={placeholder}
      options={locations}
      onChange={handleLocationChange}
      value={value}
    />
  );
}
